import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AssignAuthSupportRequestBody, AuthSupport, AuthSupportCreate, AuthSupportPagedList, AuthSupportSearch, AuthSupportUpdate } from 'src/swagger-gen/web-secured/models';
import { BaseService } from '../services/base.service';

@Injectable({
  providedIn: 'root',
})
export class BadgeRepo extends BaseService {
  badgeUrl = environment.apiUrl + '/badges';
  readonly BADGES_LIMIT = '20';

  constructor(private http: HttpClient, private logger: NGXLogger) {
    super();
  }

  /**
   * Get all badges
   */
  getBadgeList(
    offset?: number,
    limit?: number,
    params?: any[],
    body?: AuthSupportSearch
  ): Observable<AuthSupportPagedList> {
    const url = this.badgeUrl + '/search';
    let requestParams: any;
    let requestString = this.pagination(offset, limit, this.BADGES_LIMIT);
    let queryParams = '';
    params.forEach((p: any) => {
      queryParams += '&' + p.key + '=' + p.value;
    });
    requestString += queryParams;
    requestParams = new HttpParams({ fromString: requestString });
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: requestParams,
    };

    const bodyReq = body ? body : { filters: [] };
    return this.http.post<AuthSupportPagedList>(url, bodyReq, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Get badge list from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  /**
   * Get badge by uid
   */
  getBadge(uid: string): Observable<AuthSupport> {
    const url = this.badgeUrl + `/${uid}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.get<AuthSupport>(url, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Get a badge from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  exportBadgeDefinition(): Observable<any> {
    const url = `${this.badgeUrl}/export`;

    const httpOptions: any = {
      headers: this.httpHeaders(),
      responseType: 'blob' as 'json',
      observe: 'response',
    };

    return this.http
      .get<any>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  importBadgeDefinition(file: any): Observable<any> {
    const url = `${this.badgeUrl}/import`;

    const httpOptions: any = {
      headers: new HttpHeaders().set(
        'Content-Type',
        'application/vnd.ms-excel'
      ),
      observe: 'response',
    };

    return this.http
      .post<any>(url, file, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  /**
   * Create a new badge.
   * - `BadgeCreate body`: The badge create object
   */
  createBadge(bodyParams: AuthSupportCreate): Observable<any> {
    const url = this.badgeUrl;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      observe: 'response' as 'body'
    };

    return this.http
      .post<any>(url, bodyParams, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  /**
   * Update an existing badge.
   * - `uid`: The badge ID. Must be a valid ID.
   * - `badgeUpdate`: The badge update object
   */
  updateBadge(uid: string, bodyParams: AuthSupportUpdate): Observable<any> {
    const url = this.badgeUrl + `/${uid}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .put<any>(url, bodyParams, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  deleteBadge(uid: string): Observable<any> {
    const url = this.badgeUrl + '/' + uid;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .delete<any>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  assignBadgeToUser(
    userUid: string,
    subscriptionPlanUid: string,
    body: AssignAuthSupportRequestBody
  ): Observable<any> {
    const url = `${environment.apiUrl}/subscriptions/${subscriptionPlanUid}/users/${userUid}/auth-supports/assign`;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .post<any>(url, body, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  unassignBadgeToUser(
    userUid: string,
    subscriptionPlanUid: string,
    body: any
  ): Observable<any> {
    const url = `${environment.apiUrl}/subscriptions/${subscriptionPlanUid}/users/${userUid}/auth-supports/remove`;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .post<any>(url, body, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  assignBadgeToVehicle(
    vehicleUid: string,
    subscriptionPlanUid: string,
    body: AssignAuthSupportRequestBody
  ): Observable<any> {
    const url = `${environment.apiUrl}/custom/subscriptions/${subscriptionPlanUid}/vehicles/${vehicleUid}/auth-supports/assign`;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .post<any>(url, body, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  unassignBadgeToVehicle(
    vehicleUid: string,
    subscriptionPlanUid: string,
    body: any
  ): Observable<any> {
    const url = `${environment.apiUrl}/custom/subscriptions/${subscriptionPlanUid}/vehicles/${vehicleUid}/auth-supports/remove`;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .post<any>(url, body, httpOptions)
      .pipe(catchError(this.handleServerError));
  }
}
